import React from "react";
import { Box } from "@mui/material";
import formatDate from "../../_src_shared/utils/functions/formatDate";
import T from "./submissionTableTokens";
import BorrowerCell from "./components/BorrowerCell";
import GroupHeaderCell from "./components/GroupHeaderCell";
import DownloadSubmissionCell from "./components/DownloadSubmissionCell";
import { Dash, StatusCell, TypeBadge } from "./components/tableChips";
import {
  submissionBorrowerName,
  submissionIsAssigned,
  submissionPropertyAddress,
  submissionTime,
} from "./submissionHelpers";

/**
 * Borrower · Type · Location · Submitted · Status · download.
 *
 * Row actions deliberately do NOT live in a column — they're in the expanded
 * panel. The one exception is Status, which IS the call to action when a
 * submission still needs a loan.
 *
 * GROUP ROWS: with "Group by borrower" on, the row list is interleaved with
 * `{ __group: true, ... }` entries. Rather than AG Grid's own row grouping —
 * which cannot be combined with master/detail, and master/detail is what the
 * expanded submission panel is built on — a group row is an ordinary row whose
 * first column spans the full width via `colSpan`. Every other column renders
 * nothing for it, because the span is already drawing over them.
 */

// date-fns "MMM d, yyyy" in the original → the dayjs equivalent here.
const submittedLabel = (v) => formatDate(v, null, "MMM D, YYYY");

/** A group header row rather than a submission. */
export const isGroupRow = (data) => Boolean(data?.__group);

/**
 * Chips are inline-flex spans, so without this they sit on the cell's text
 * baseline near the top of a two-line row rather than centred against it. Plain
 * text cells look centred on their own; a chip does not.
 */
const CENTERED_CELL = {
  display: "flex",
  alignItems: "center",
  height: "100%",
};

// Skip a cell on group rows — the spanned first column covers that space.
const blankOnGroup = (render) => (p) => (isGroupRow(p.data) ? null : render(p));

const TOTAL_COLUMNS = 6;

export const buildSubmissionColumns = ({
  onAssign,
  onCreateLoan,
  onToggleGroup,
  currentUserId,
  grouped = false,
} = {}) => [
  {
    headerName: "Borrower",
    field: "borrowerName",
    flex: 2.2,
    minWidth: 240,
    // The group header takes the whole row.
    colSpan: (p) => (isGroupRow(p.data) ? TOTAL_COLUMNS : 1),
    cellRendererSelector: (p) =>
      isGroupRow(p.data)
        ? { component: GroupHeaderCell, params: { onToggleGroup } }
        : {
            // agGroupCellRenderer supplies the master/detail expander; the
            // borrower identity renders inside it.
            component: "agGroupCellRenderer",
            params: { innerRenderer: BorrowerCell, currentUserId },
          },
    // Indent has to sit on the CELL, not on the inner renderer: the master/
    // detail expander is drawn by agGroupCellRenderer to the left of whatever
    // the inner renderer returns, so padding the content would leave the
    // chevron stranded at the group's own margin.
    cellStyle: (p) => ({
      display: "flex",
      alignItems: "center",
      height: "100%",
      paddingLeft: p.data?.__inGroup ? "38px" : undefined,
    }),
    valueGetter: (p) =>
      isGroupRow(p.data) ? p.data.name : submissionBorrowerName(p.data),
  },
  {
    headerName: "Type",
    field: "__type",
    flex: 0.9,
    minWidth: 130,
    cellStyle: CENTERED_CELL,
    cellRenderer: blankOnGroup((p) => <TypeBadge type={p.data?.__type} />),
  },
  {
    headerName: "Location",
    field: "location",
    flex: 1.9,
    minWidth: 200,
    valueGetter: (p) =>
      isGroupRow(p.data) ? null : submissionPropertyAddress(p.data),
    cellRenderer: blankOnGroup((p) =>
      p.value ? (
        <Box
          sx={{
            fontSize: 13,
            color: T.ink700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {p.value}
        </Box>
      ) : (
        <Dash />
      ),
    ),
  },
  {
    headerName: "Submitted",
    field: "submission_date",
    flex: 1,
    minWidth: 120,
    // The table opens newest-first. Sort on the instant, render the day. Only
    // meaningful ungrouped — see the sortable note below.
    sort: grouped ? null : "desc",
    valueGetter: (p) => submissionTime(p.data),
    cellRenderer: blankOnGroup((p) => (
      <Box sx={{ fontSize: 13, color: T.ink700 }}>
        {submittedLabel(p.data?.submission_date) || "—"}
      </Box>
    )),
  },
  {
    headerName: "Status",
    // Wide enough for the two-chip unassigned state (Assign To Loan + Create
    // Loan) to sit on one line.
    flex: 1.6,
    minWidth: 235,
    cellStyle: CENTERED_CELL,
    // Needs-a-loan sorts first — it's the column's whole point.
    valueGetter: (p) => (submissionIsAssigned(p.data) ? 1 : 0),
    cellRenderer: blankOnGroup((p) => (
      <StatusCell
        row={p.data}
        onAssign={onAssign}
        onCreateLoan={onCreateLoan}
      />
    )),
  },
  {
    headerName: "",
    flex: 0.4,
    minWidth: 48,
    maxWidth: 56,
    sortable: false,
    filter: false,
    resizable: false,
    cellRenderer: blankOnGroup(DownloadSubmissionCell),
  },
];

/**
 * Grouped rows are ORDERED, not sortable: the group headers are ordinary rows
 * sitting immediately above their children, so any re-sort would scatter them
 * away from the group they head. The list already arrives newest-first (groups
 * by their newest submission, rows within a group newest-first), so nothing is
 * lost — the toggle is what you turn off to sort.
 */
export const submissionsDefaultColDef = (grouped) => ({
  sortable: !grouped,
});

export default buildSubmissionColumns;
